"use client";

import { useState, useMemo } from "react";
import Image from "next/image";
import Link from "next/link";
import {
  Search,
  ChevronDown,
  Mail,
  Phone,
  HelpCircle,
  Zap,
  ShieldCheck,
  Calendar,
  Sparkles,
  ArrowRight,
  CheckCircle2,
} from "lucide-react";
import { FAQ_LIST, FAQItem } from "./faq-data";

export default function FaqClient() {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [openIndexes, setOpenIndexes] = useState<Record<number, boolean>>({ 0: true, 1: true });

  const categories = [
    { id: "all", label: "All Questions" },
    { id: "aggregators", label: "AI Aggregator Intake" },
    { id: "mot", label: "DVSA MOT & Reminders" },
    { id: "crm", label: "CRM & Advisories" },
    { id: "calendar", label: "Google Calendar Sync" },
    { id: "pricing", label: "Pricing & Plans" },
  ];

  const filteredFaqs = useMemo(() => {
    return FAQ_LIST.filter((faq) => {
      const matchesCategory =
        selectedCategory === "all" || faq.category === selectedCategory;
      const matchesQuery =
        searchQuery.trim() === "" ||
        faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
        faq.answer.toLowerCase().includes(searchQuery.toLowerCase()) ||
        faq.categoryLabel.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesCategory && matchesQuery;
    });
  }, [searchQuery, selectedCategory]);

  const toggleIndex = (index: number) => {
    setOpenIndexes((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  return (
    <div className="bg-slate-50 font-sans text-slate-900 min-h-screen">
      {/* ── 1. HERO SEARCH SECTION ────────────────────────────────────────── */}
      <section className="relative overflow-hidden bg-linear-to-br from-slate-950 via-slate-900 to-blue-950 text-white pt-16 pb-20 md:pt-20 md:pb-24">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl pointer-events-none"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-teal-500/15 rounded-full blur-3xl pointer-events-none"></div>
        <div className="absolute inset-0 bg-[radial-gradient(#3b82f6_1px,transparent_1px)] [background-size:24px_24px] opacity-10 pointer-events-none"></div>

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 text-center space-y-6">
          <span className="inline-block px-4 py-1.5 rounded-full bg-blue-500/15 border border-blue-400/30 text-blue-300 text-xs font-bold uppercase tracking-widest">
            UK GARAGE HELP CENTER & FAQS
          </span>

          <h1 className="text-3xl sm:text-5xl lg:text-6xl font-black leading-tight text-white tracking-tight">
            Frequently Asked <span className="bg-linear-to-r from-blue-400 via-cyan-300 to-teal-300 bg-clip-text text-transparent">Questions</span>
          </h1>

          <p className="text-base sm:text-lg md:text-xl text-slate-300 max-w-2xl mx-auto leading-relaxed font-normal">
            Everything you need to know about Garage Boost: AI aggregator email intake, DVSA MOT sync, 30-14-7 day reminders, pricing, and setup.
          </p>

          {/* Search Bar */}
          <div className="max-w-2xl mx-auto pt-2">
            <div className="relative">
              <Search
                size={20}
                className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
              />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search questions (e.g., BookMyGarage, DVSA sync, pricing, SMS reminders)..."
                className="w-full bg-white/10 backdrop-blur-md border border-white/20 text-white placeholder-slate-400 rounded-2xl py-4 pl-12 pr-4 text-sm sm:text-base focus:outline-hidden focus:ring-2 focus:ring-teal-400 focus:bg-slate-900/90 transition-all shadow-xl"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-xs bg-slate-800 text-slate-300 hover:text-white px-2.5 py-1 rounded-md"
                >
                  Clear
                </button>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ── 2. CATEGORY PILLS ──────────────────────────────────────────────── */}
      <section className="bg-white border-b border-slate-200 sticky top-16 z-20 shadow-xs">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center gap-2 overflow-x-auto no-scrollbar pb-1 sm:pb-0 justify-start sm:justify-center">
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(cat.id)}
                className={`whitespace-nowrap px-4 py-2 rounded-xl text-xs sm:text-sm font-bold transition-all ${
                  selectedCategory === cat.id
                    ? "bg-blue-600 text-white shadow-md shadow-blue-500/20"
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200 hover:text-slate-900"
                }`}
              >
                {cat.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* ── 3. FAQ ACCORDION LIST & SIDEBAR ────────────────────────────────── */}
      <section className="py-14 md:py-20 max-w-5xl mx-auto px-4 sm:px-6">
        <div className="space-y-4">
          {filteredFaqs.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-3xl border border-slate-200 p-8">
              <HelpCircle size={48} className="mx-auto text-slate-300 mb-3" />
              <h3 className="text-xl font-bold text-slate-800">No matching questions found</h3>
              <p className="text-slate-500 text-sm mt-1 mb-6">
                Try a different keyword or browse all questions.
              </p>
              <button
                onClick={() => {
                  setSearchQuery("");
                  setSelectedCategory("all");
                }}
                className="bg-blue-600 text-white font-bold px-6 py-2.5 rounded-xl hover:bg-blue-700 transition"
              >
                Reset Search
              </button>
            </div>
          ) : (
            filteredFaqs.map((faq, idx) => {
              const isOpen = !!openIndexes[idx];
              return (
                <div
                  key={idx}
                  className="bg-white rounded-2xl border border-slate-200 shadow-xs overflow-hidden transition-all duration-200 hover:border-blue-400/60"
                >
                  <button
                    onClick={() => toggleIndex(idx)}
                    className="w-full text-left p-5 sm:p-6 flex items-start justify-between gap-4 focus:outline-hidden"
                  >
                    <div className="space-y-1">
                      <span className="inline-block text-[11px] font-bold text-blue-600 uppercase tracking-wider bg-blue-50 px-2.5 py-0.5 rounded-md mb-1">
                        {faq.categoryLabel}
                      </span>
                      <h3 className="text-base sm:text-lg font-bold text-slate-900 leading-snug">
                        {faq.question}
                      </h3>
                    </div>
                    <div
                      className={`p-2 rounded-xl bg-slate-100 text-slate-600 transition-transform duration-200 shrink-0 ${
                        isOpen ? "rotate-180 bg-blue-100 text-blue-600" : ""
                      }`}
                    >
                      <ChevronDown size={18} />
                    </div>
                  </button>

                  {isOpen && (
                    <div className="px-5 sm:px-6 pb-6 pt-1 text-slate-600 text-sm sm:text-base leading-relaxed border-t border-slate-100 bg-slate-50/50">
                      <p>{faq.answer}</p>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* ── 4. LIVE DASHBOARD SHOWCASE CARD ──────────────────────────────── */}
        <div className="mt-16 bg-slate-950 rounded-3xl p-6 sm:p-10 text-white border border-slate-800 shadow-2xl">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-6 space-y-4">
              <span className="inline-block px-3 py-1 rounded-full bg-blue-500/15 border border-blue-400/30 text-blue-300 text-xs font-bold uppercase tracking-widest">
                HOW IT WORKS IN ACTION
              </span>
              <h2 className="text-2xl sm:text-3xl font-black text-white">
                Experience Modern UK Garage Automation
              </h2>
              <p className="text-slate-300 text-sm leading-relaxed">
                Connect your Gmail in 2 minutes, auto-ingest aggregator booking emails, sync Google Calendar in real-time, and let our automated DVSA reminder engine increase your workshop revenue.
              </p>
              <div className="pt-2 flex flex-col sm:flex-row gap-3">
                <Link
                  href="/signup"
                  className="bg-linear-to-r from-blue-600 to-teal-600 hover:from-blue-500 hover:to-teal-500 text-white font-bold px-6 py-3 rounded-xl shadow-lg shadow-blue-500/30 text-center flex items-center justify-center gap-2"
                >
                  Start 14-Day Free Trial <ArrowRight size={16} />
                </Link>
                <Link
                  href="/pricing"
                  className="border border-white/20 hover:bg-white/10 text-white font-bold px-6 py-3 rounded-xl text-center"
                >
                  View Plans & Pricing
                </Link>
              </div>
            </div>

            <div className="lg:col-span-6">
              <div className="rounded-2xl overflow-hidden border border-slate-800 shadow-xl">
                <Image
                  src="/images/dashboard/garage-command-center.png"
                  alt="Garage Boost Live Command Center Dashboard"
                  width={800}
                  height={500}
                  className="w-full h-auto object-cover"
                />
              </div>
            </div>
          </div>
        </div>

        {/* ── 5. STILL HAVE QUESTIONS CONTACT CARD ──────────────────────────── */}
        <div className="mt-12 bg-linear-to-r from-blue-600 to-indigo-700 rounded-3xl p-8 sm:p-10 text-white text-center sm:text-left flex flex-col sm:flex-row items-center justify-between gap-6 shadow-xl">
          <div className="space-y-2">
            <h3 className="text-2xl font-black text-white">Have a specific question not covered here?</h3>
            <p className="text-blue-100 text-sm sm:text-base max-w-xl">
              Our UK support team is available Monday to Friday, 8am – 6pm to assist with setup, API connections, and garage workflows.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 shrink-0">
            <a
              href="tel:07380448187"
              className="bg-white text-blue-900 hover:bg-blue-50 font-bold px-6 py-3 rounded-xl transition flex items-center justify-center gap-2 shadow-md"
            >
              <Phone size={16} className="text-blue-600" /> Call 07380 448187
            </a>
            <Link
              href="/contact"
              className="border border-white/40 hover:bg-white/10 text-white font-bold px-6 py-3 rounded-xl transition flex items-center justify-center gap-2"
            >
              <Mail size={16} /> Contact Sales
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
